import Container from '@ui/container/container';
import useLoadRSVPLinkSync from '@/connection/hooks/use-load-rsvp-link-sync';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { useState } from 'react';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import Switch from '@ui/switch/switch';

import styles from './registration-toggle.module.css';

export default function RegistrationToggle() {
  useLoadRSVPLinkSync();
  const dispatch = useAppDispatch();

  const workingProject = useAppSelector(getWorkingProject);
  const {
    registrationOn,
    registrationRequired,
    containsBreakoutOrFiresideSession,
    directoryOn,
    messagingIsOn,
    myListIsOn,
    leaderboardEnabled,
    watermarkOn,
    containsPrivateResponses,
    courseOn,
    muxDrmEnabled,
    rsvpLinkSyncApproved,
  } = useComputedRegistrationSettings();
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);

  const toggleRegistration = (on: boolean) => {
    if (!workingProject) {
      return;
    }

    if (registrationRequired && registrationOn) {
      // registration required message visible, user clicked OK
      setShowRegistrationModal(false);
      return;
    }
    dispatch(saveWorkingProjectByFields({ registration_on: on }));
    setShowRegistrationModal(false);
  };

  const registrationToggleWarning = () => {
    const warning = [];

    // Turning on the registration
    if (!registrationOn) {
      warning.push(
        <p key="registration-on">
          Turning on registration will require your visitors to complete a form before coming to the page. A
          registration page is recommended if you are gating your event or looking to capture more information about
          attendees.
        </p>
      );
      return warning;
    }

    // Turning off the registration and there are still features that need to be disabled
    if (registrationRequired) {
      warning.push(
        <p key="contains-breakot-or-fireside" className="registration-warning-heading">
          Before you can turn off registration, you must disable the following features:
        </p>
      );
    }

    // Turning off the registration
    else {
      warning.push(
        <p key="registration-off">
          If you turn the registration off, it will become public and everyone will see the event sessions.
        </p>
      );
      return warning;
    }

    // Features that need to be disabled
    if (containsBreakoutOrFiresideSession) {
      warning.push(
        <dl key="reg-warning-1" className={styles.registrationWarning}>
          <dt>Breakouts or Fireside sessions: </dt>
          <dd>Pages ➜ Watch ➜ [Fireside/Breakout session] ➜ Three Dots ➜ Delete Session </dd>
        </dl>
      );
    }
    if (directoryOn) {
      warning.push(
        <dl key="reg-warning-2" className={styles.registrationWarning}>
          <dt>Directory: </dt>
          <dd>Social ➜ Directory ➜ Enable Directory</dd>
        </dl>
      );
    }
    if (messagingIsOn) {
      warning.push(
        <dl key="reg-warning-3" className={styles.registrationWarning}>
          <dt>Messaging: </dt>
          <dd>Social ➜ Messaging ➜ Direct Messaging</dd>
        </dl>
      );
    }
    if (myListIsOn) {
      warning.push(
        <dl key="reg-warning-4" className={styles.registrationWarning}>
          <dt>Watchlist Agenda Builder: </dt>
          <dd>Pages ➜ Settings ➜ Watchlist Agenda Builder</dd>
        </dl>
      );
    }
    if (leaderboardEnabled) {
      warning.push(
        <dl key="reg-warning-5" className={styles.registrationWarning}>
          <dt>Leaderboard: </dt>
          <dd>Social ➜ Leaderboard ➜ Leaderboard Page Toggle</dd>
        </dl>
      );
    }
    if (courseOn) {
      warning.push(
        <dl key="reg-warning-6" className={styles.registrationWarning}>
          <dt>Course: </dt>
          <dd>Project Details ➜ Course</dd>
        </dl>
      );
    }
    if (watermarkOn) {
      warning.push(
        <dl key="reg-warning-7" className={styles.registrationWarning}>
          <dt>Watermark: </dt>
          <dd>Pages ➜ Watch ➜ [Session] ➜ Settings ➜ End-User Watermarking</dd>
        </dl>
      );
    }
    if (muxDrmEnabled) {
      warning.push(
        <dl key="reg-warning-8" className={styles.registrationWarning}>
          <dt>DRM Video: </dt>
          <dd>Reach out to your CSM to disable this for your channel</dd>
        </dl>
      );
    }
    if (containsPrivateResponses) {
      warning.push(
        <dl key="reg-warning-8" className={styles.registrationWarning}>
          <dt>Private Question Responses: </dt>
          <dd>Pages ➜ Watch ➜ Session Page ➜ Engage ➜ Question</dd>
        </dl>
      );
    }

    // Returning the array of warnings
    return warning;
  };

  return (
    <Container
      design="GlobalCont1"
      style={{
        borderRadius: '12px',
      }}
      className="flex ai-center jc-space-between padding-16"
    >
      <Text tag="h6" className="H6">
        Enable Registration
      </Text>
      <Switch
        value={registrationOn}
        on={registrationOn}
        onClick={() => setShowRegistrationModal(true)}
        disabled={registrationOn && (rsvpLinkSyncApproved || muxDrmEnabled)}
      />
      <Modal
        open={showRegistrationModal}
        customSize={{
          width: '500px',
          height: 'auto',
          maxHeight: '500px',
        }}
        onRequestClose={() => setShowRegistrationModal(false)}
        modalClassName="padding-24 display-flex flex-column gap-16"
        renderHeaderContent={() => (
          <Text tag="h3" className="H3 margin-b-24">
            {registrationOn
              ? containsBreakoutOrFiresideSession
                ? 'Registration Required'
                : 'Open the Event to Everyone?'
              : 'Turn on Registration'}
          </Text>
        )}
        renderFooterContent={() => (
          <div className="display-flex flex-row gap-16 jc-flex-end">
            {!registrationRequired && <button onClick={() => setShowRegistrationModal(false)}>Cancel</button>}
            <Button design={TButtonAndLinkDesigns.LocalBttnPrim} onClick={() => toggleRegistration(!registrationOn)}>
              {registrationOn ? (registrationRequired ? 'OK' : 'Open Event') : 'Enable Registration'}
            </Button>
          </div>
        )}
      >
        <div>{registrationToggleWarning()}</div>
      </Modal>
    </Container>
  );
}
